import { useEffect, useState } from "react";
import OtpInput from "react-otp-input";
import { t } from "@/i18n-js/instance";
import { useTailwindSmScreenMediaQuery } from "@/react/hooks/useMediaQuery";

interface OTPInputProps {
  disabled?: boolean;
  error?: boolean;
  onComplete?: (otp: string) => void;
  onValueChange?: (otp: string) => void;
}

export const OTPInput = ({
  onComplete,
  onValueChange,
  disabled = false,
  error = false,
}: OTPInputProps) => {
  const [otp, setOtp] = useState("");
  const isDesktop = useTailwindSmScreenMediaQuery();

  const handleChange = (value: string) => {
    setOtp(value);
    onValueChange?.(value);

    if (value.length === 6) {
      onComplete?.(value);
    }
  };

  useEffect(() => {
    if (error) {
      setOtp("");
    }
  }, [error]);

  const getInputStyle = () => {
    const baseStyle = {
      width: isDesktop ? "56px" : "48px",
      height: isDesktop ? "56px" : "48px",
      border: "1px solid",
      borderRadius: "8px",
      fontSize: isDesktop ? "18px" : "16px",
      lineHeight: isDesktop ? "28px" : "24px",
      fontWeight: "500",
      textAlign: "center" as const,
      outline: "none",
      transition: "all 0.15s ease-in-out",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "var(--color-white)",
      color: "var(--color-text-darkest)",
    };

    if (disabled) {
      return {
        ...baseStyle,
        borderColor: "var(--color-border-tertiary)",
        backgroundColor: "var(--color-background-secondary)",
        cursor: "not-allowed",
        color: "var(--color-text-disabled)",
      };
    }

    if (error) {
      return {
        ...baseStyle,
        borderColor: "var(--color-feedback-error)",
      };
    }

    return {
      ...baseStyle,
      borderColor: "var(--color-border-primary)",
      boxShadow: "none",
    };
  };

  const getFocusStyle = () => {
    if (error) {
      return {
        borderColor: "var(--color-feedback-error)",
        boxShadow: "0 0 0 2px rgba(220, 38, 38, 0.2)",
      };
    }

    return {
      borderColor: "var(--color-border-light)",
    };
  };

  return (
    <fieldset className="contents">
      <legend className="sr-only">
        {t(
          "paywall_checkout.form.email_verification_states.otp_state.verification_code_legend",
        )}
      </legend>
      <OtpInput
        value={otp}
        onChange={handleChange}
        numInputs={6}
        separator={<span className="w-[.65rem] sm:w-[1.9rem]" />}
        isInputNum
        shouldAutoFocus={!disabled}
        inputStyle={getInputStyle()}
        focusStyle={getFocusStyle()}
        isDisabled={disabled}
      />
    </fieldset>
  );
};
